import {Divider, Grid, KeyValue, TextFieldForm, Typography} from 'web-library';
import {useTranslation} from 'react-i18next';

import {makeStyles} from 'tss-react/mui';
import {useHistory} from 'react-router-dom';
import React, {useState} from 'react';
import {MutationStatus} from 'react-query';
import FormHeader from '../../../../components/form-items/FormHeader';
import PaymentButton, {PaymentMethod} from '../PaymentButton';
import RouteKey from '../../../../routes/key';
import PayNowProxyDetailsModal
  from '../../../subject/components/SubjectAmendment/PayNowProxyDetailsModal';
import {
  SchoolCandidateAppealRegistrationResponse
} from "../../../../api-refactored/school-candidate-payment/usePostRetrieveSchoolCandidateAppealRegistration";
import {
  useGetSchoolCandidatePaymentAccess
} from "../../../../api-refactored/school-candidate-payment/useGetSchoolCandidatePaymentAccess";
import {
  usePostSchoolCandidatePaymentPayNowProxyDetails
} from "../../../../api-refactored/school-candidate-payment/usePostSchoolCandidatePaymentPayNowProxyDetails";
import {fallbackEmptyString} from "../../../../utils/StringUtils";
import {RetrieveAppealRegistrationFormData} from "./schema/schemaRetrieveAppealRegistrationDetails";
import {useForm, UseFormSetValue} from "react-hook-form";
import {joiResolver} from "@hookform/resolvers/joi";
import {
  schemaSchoolCandidateAppealRegistrationPayment,
  SchoolCandidateAppealRegistrationPaymentFormData
} from "./schema/schemaSchoolCandidateAppealRegistrationPayment";
import {
  usePostSchoolCandidateAppealConfirmAndPay
} from "../../../../api-refactored/school-candidate-payment/usePostSchoolCandidateAppealConfirmAndPay";

const useStyles = makeStyles()((theme) =>
    ({
      input: {
        marginBottom: theme.spacing(4),
      },
      paymentButton: {
        padding: theme.spacing(2),
        marginRight: theme.spacing(2),
      },
    }));
const parseAmountBeforeInput = (numInString: string | null, e: any, setValue: UseFormSetValue<SchoolCandidateAppealRegistrationPaymentFormData>) => {
  if (numInString === null) {
    return ''
  }
  const num = parseFloat(numInString);
  if (Number.isNaN(num)) {
    return '';
  }

  if (num === 0) {
    e.target.select();
    setValue('amount', num);
  }

  if (Math.ceil(num) <= 0) {
    return '';
  }
  setValue('amount', parseFloat(num.toFixed(2)));
};
const parseAmountAfterInput = (numInString: string, setValue: UseFormSetValue<SchoolCandidateAppealRegistrationPaymentFormData>) => {
  const num = parseFloat(numInString);
  if (Number.isNaN(num)) {
    return '';
  }
  if (Math.abs(num) !== num) {
    return '';
  }
  setValue('amount', parseFloat(num.toFixed(2)));
};

interface SchoolCandidateAppealPaymentDetailsProps {
  appealRegistration: SchoolCandidateAppealRegistrationResponse;
  candidateInfo: RetrieveAppealRegistrationFormData;
}

const {payment} = RouteKey.child;
const {schoolCandidatePayment} = RouteKey.payment.child;
const {receipt} = schoolCandidatePayment.child;

const SchoolCandidateAppealPaymentDetails = ({
                                               appealRegistration,
                                               candidateInfo
                                             }: SchoolCandidateAppealPaymentDetailsProps) => {
  const {t} = useTranslation();
  const {classes} = useStyles();
  const history = useHistory();
  const [isPaymentDisabled, setIsPaymentDisabled] = useState(false);
  const [paymentAccessDetailsOption, setPaymentAccessDetailsOption] = useState<
      Array<PaymentMethod>
  >([]);

  const [openPayNowProxyModal, setOpenPayNowProxyModal] = useState<boolean>(false);
  const [apiButtonStatus, setApiButtonStatus] = useState<MutationStatus>('idle');

  const [paymentCallback, setPaymentCallback] = useState<
      (internalTransactionRef?: string) => void
  >();
  const [paynowProxyOnCloseCallback, setPaynowProxyOnCloseCallback] = useState<
      (paymentMethod: PaymentMethod) => void
  >();
  const [paymentMethod, setPaymentMethod] = useState<string>('');

  useGetSchoolCandidatePaymentAccess({
    organisationUuid: candidateInfo.school.code,
    enabled: !!candidateInfo.school.code,
    onSuccess: (res) => {
      const paymentAccessDetailsArray = [];
      if (res.bankTransferMethod) {
        paymentAccessDetailsArray.push(PaymentMethod.BANK_TRANSFER);
      }
      if (res.creditCardMethod) {
        paymentAccessDetailsArray.push(PaymentMethod.ENETS_CREDIT_CARD);
      }
      if (res.payNowMethod) {
        paymentAccessDetailsArray.push(PaymentMethod.PAYNOW_QR);
      }
      setPaymentAccessDetailsOption(paymentAccessDetailsArray);
    },
    onError: (_) => {
      setPaymentAccessDetailsOption([]);
    },
  });

  const {mutate: schoolCandidateAppealConfirmAndPayMutate} = usePostSchoolCandidateAppealConfirmAndPay({});

  const {mutate: postPayNowProxyDetails} = usePostSchoolCandidatePaymentPayNowProxyDetails({
    onSuccess: () => {
      setApiButtonStatus('success');
      setOpenPayNowProxyModal(false);
      schoolCandidateAppealConfirmAndPayMutate(
          {
            uuid: appealRegistration.uuid,
            paymentMode: fallbackEmptyString(paymentMethod),
            amount: form.getValues("amount")
          },
          {
            onSuccess: (res) => {
              if (res.paymentSuccess) {
                history.push(`/${payment}/${schoolCandidatePayment.route}/${appealRegistration?.uuid}/${receipt.route}`);
              }
              if (paymentCallback) {
                paymentCallback(res.internalTransactionRef);
              }
            },
            onError: () => {
              if (paymentCallback) {
                paymentCallback('');
              }
            },
          }
      );
    },
    onError: () => setApiButtonStatus('error'),
  });

  const handleClickPaymentButtonTest = (callback: () => void) => {
    callback();
  };

  const handlePaymentOption = (
      callback: (internalTransactionRef: string) => void,
      method: string,
      onClosePaynowProxyCallback?: (paymentMethod: PaymentMethod) => void
  ) => {
    setPaymentMethod(method);
    if (PaymentMethod.PAYNOW_QR === method) {
      setOpenPayNowProxyModal(true);
      setPaymentCallback(() => callback);
      setPaynowProxyOnCloseCallback(() => onClosePaynowProxyCallback);
    } else {
      schoolCandidateAppealConfirmAndPayMutate(
          {
            uuid: appealRegistration.uuid,
            paymentMode: fallbackEmptyString(method),
            amount: form.getValues("amount")
          },
          {
            onSuccess: (res) => {
              if (res.paymentSuccess) {
                history.push(`/${payment}/${schoolCandidatePayment.route}/${appealRegistration?.uuid}/${receipt.route}`);
              }
              callback(res.internalTransactionRef);
            },
            onError: () => {
              callback('');
            },
          }
      );
    }
  };

  const onClosePayNowProxyModal = () => {
    if (paynowProxyOnCloseCallback) {
      paynowProxyOnCloseCallback(PaymentMethod.PAYNOW_QR);
    }
  };

  const form = useForm<SchoolCandidateAppealRegistrationPaymentFormData>({
    mode: 'onSubmit',
    resolver: joiResolver(schemaSchoolCandidateAppealRegistrationPayment),
  });
  const {formState: {errors}, setValue, handleSubmit} = form;

  return (
      <>
        <Divider/>
        <form onSubmit={handleSubmit((_e) => null)}>
          <FormHeader title={t('components->>SchoolCandidateAppealPayment.PaymentDetailsTitle')}/>
          <Typography className={classes.input} variant="body1">
            {t('components->>SchoolCandidateAppealPayment.PaymentDetailsInstruction')}
          </Typography>
          <KeyValue
              id="schoolCandidatePayment-candidateName"
              keyText={t('components->>SchoolCandidateAppealPayment.PaymentDetailsName')}
              valueText={appealRegistration?.name}
          />
          <KeyValue
              id="schoolCandidatePayment-examYear"
              keyText={t('components->>SchoolCandidateAppealPayment.PaymentDetailsExamYear')}
              valueText={appealRegistration?.examYear?.toString()}
          />


          <TextFieldForm
              className={classes.input}
              label={t(`components->>SchoolCandidateAppealPayment.AmountToPay`)}
              placeholder={t(`components->>SchoolCandidateAppealPayment.EnterAmountToPay`)}
              name="amount"
              variant="outlined"
              error={!!errors.amount}
              helperText={errors?.amount?.message || ''}
              fullWidth
              form={form}
              isRequired
              type="number"
              inputProps={{startAdornment: "$"}}
              onBlur={(e) => parseAmountAfterInput(e.target.value, setValue)}
              onFocusCapture={(e) => parseAmountBeforeInput(e.target.textContent, e, setValue)}
          />

          <Grid container direction="row" alignItems="flex-start" spacing={3}>
            <Grid xs={12} md={4}>
              <PaymentButton
                  buttonClassName={classes.paymentButton}
                  isUniqueLink={false}
                  disabled={isPaymentDisabled}
                  onPaymentComplete={(isRetry) => {
                    setIsPaymentDisabled(isRetry);
                  }}
                  paymentMethodsAvailable={paymentAccessDetailsOption}
                  onClickPaymentButton={async (callback) => {
                    if(await form.trigger()){
                      handleClickPaymentButtonTest(callback);
                    }
                  }}
                  onHandlePaymentMethod={(callback, method, paymentModalOnClose) => {
                    handlePaymentOption(callback, method, paymentModalOnClose);
                  }}
                  onSuccessPayment={() => {
                    history.push(
                        `/${payment}/${schoolCandidatePayment.route}/${appealRegistration?.uuid}/${receipt.route}`
                    );
                  }}
                  fullWidth
                  buttonText={t('exam->>ReviewExam.ConfirmAndPay')}
                  buttonVariant="contained"
                  buttonErrorText={t('components->>SchoolCandidateAppealPayment.PaymentErrorButtonLabel')}
                  customPaymentMessage={t('components->>SchoolCandidateAppealPayment.PaymentMessage')}
              />
            </Grid>
          </Grid>
          <PayNowProxyDetailsModal
              modalState={openPayNowProxyModal}
              setOpenModalState={setOpenPayNowProxyModal}
              postPayNowProxyDetails={postPayNowProxyDetails}
              applicationUuid={appealRegistration.uuid}
              apiButtonStatus={apiButtonStatus}
              setApiButtonStatus={setApiButtonStatus}
              onClosePayNowProxyModal={onClosePayNowProxyModal}
              modalInstruction={t('components->>SchoolCandidateAppealPayment.PayNowProxyInstruction')}
          />
        </form>
      </>
  );
};

export default SchoolCandidateAppealPaymentDetails;
